/**
 * Regra de negócio única para "este consultor conta para a meta N do gestor?".
 *
 * Usada por: useVisaoComissaoSimulada, useMinhaComissao, VendedoresEquipePanel
 * e (a partir da Fase 5) o exportador de Excel. Manter este helper como
 * a fonte única evita o bug histórico em que dois lugares na mesma tela
 * mostravam números diferentes (15 vs 24).
 *
 * Regras (todas precisam ser TRUE para contar):
 *   - não está em período de experiência (faturamento entra, mas não infla N)
 *   - não pertence à equipe Diretoria (faturamento conta para meta, mas
 *     o usuário em si não vira "vendedor" do gestor)
 *   - não é membro de equipe Qualidade / role bko (não é consultor de campo)
 *   - não é "líder sem venda" — supervisor/coordenador *sem* venda pessoal
 *     no mês. Líder COM venda pessoal entra como vendedor normal e is_lideranca
 *     vem false da RPC, então passa neste filtro.
 *   - não é ex-funcionário NA COMPETÊNCIA (desativado ANTES do início do mês).
 *     Quem foi desligado DURANTE o mês estava na meta e continua contando — pra
 *     ele e pros gestores dele. Ex-func de verdade só aparece por causa de ESTORNO:
 *     o estorno dele deflaciona o realizado da equipe, mas ele NÃO vira cabeça do
 *     multiplicador N (senão um ex-funcionário aumentaria a meta da equipe).
 *   - não está marcado `fora_da_meta` (usuarios.fora_da_meta, Douglas 28/09/2026):
 *     consultor NULO para o gestor — nem N, nem venda (ver `ajudaNaMetaDoGestor`).
 *
 * Supervisor/coordenador com venda própria volta da RPC com is_lideranca=true
 * de 2026-09 em diante: a venda dele ajuda a equipe, mas ele não aumenta o N.
 */
export interface ConsultorParaContagem {
  em_experiencia: boolean;
  da_diretoria: boolean;
  is_lideranca: boolean;
  is_equipe_qualidade: boolean;
  /** false = ex-funcionário NA competência (desativado antes do mês). undefined/true = conta normal. */
  ativo?: boolean;
  /** usuarios.fora_da_meta — consultor nulo na meta do gestor. */
  fora_da_meta?: boolean;
}

export function contaParaMetaN(c: ConsultorParaContagem): boolean {
  return !c.em_experiencia
      && !c.da_diretoria
      && !c.is_equipe_qualidade
      && !c.is_lideranca
      && !c.fora_da_meta
      && c.ativo !== false;
}

/**
 * A venda deste consultor entra no realizado do gestor (meta e base)?
 * Qualidade não vende; `fora_da_meta` é nulo — a venda dele não ajuda ninguém.
 */
export function ajudaNaMetaDoGestor(c: { is_equipe_qualidade?: boolean; fora_da_meta?: boolean }): boolean {
  return !c.is_equipe_qualidade && !c.fora_da_meta;
}

/**
 * Estava ativo NO MÊS da competência: ainda ativo OU desativado dentro/depois
 * do mês. NUNCA usar `usuarios.ativo` cru para classificar ex-funcionário em
 * tela de competência — inativar alguém hoje apagaria o passado dele.
 *
 * @param inicioMesIso primeiro dia do mês da competência, formato 'YYYY-MM-DD'
 */
export function ativoNaCompetencia(
  u: { ativo?: boolean | null; desativado_em?: string | null },
  inicioMesIso: string,
): boolean {
  return u.ativo === true || (!!u.desativado_em && u.desativado_em >= inicioMesIso);
}

/**
 * O CONSULTOR RETENÇÃO não é vendedor — é o login da fila de retenção, na
 * equipe QUALIDADE. Ele tem meta individual mínima (1 alta) de propósito:
 * é ela que destrava o rateio da retenção entre os BKOs. Mas isso fazia ele
 * contar como "consultor que bateu meta" na comissão de quem responde pela
 * equipe QUALIDADE (Annye, Caroline, Thuane) — dinheiro de venda que não
 * existe. Ele conta para o rateio; nunca para a conta do BKO.
 */
export const FUNCAO_CONSULTOR_RETENCAO = 'CONSULTOR_RETENCAO';

export function contaParaComissaoBko(funcao?: string | null): boolean {
  return funcao !== FUNCAO_CONSULTOR_RETENCAO;
}

/**
 * CPF e CNPJ nunca somam (regra global, 14/09/2026). O gestor é medido só
 * pelos consultores do segmento dele: a supervisora da CONECTA (CPF) conta os
 * consultores CPF da equipe dela — inclusive o consultor Douglas, onde a Conecta
 * põe os pedidos —, e a gerência do CNPJ não soma a equipe CPF (Douglas
 * 21/10/2026). Equipe sem segmento conhecido é CNPJ.
 */
export function consultoresDoSegmentoDoGestor<T extends { equipe_nome?: string | null }>(
  consultores: T[],
  segmentoDoGestor: 'PF' | 'PJ',
  segmentoPorNomeEquipe: Map<string, 'PF' | 'PJ'>,
): T[] {
  return consultores.filter((c) => (segmentoPorNomeEquipe.get(String(c.equipe_nome ?? '')) ?? 'PJ') === segmentoDoGestor);
}
